import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebase";
import {
    collection, getDocs, addDoc, updateDoc, deleteDoc,
    doc, serverTimestamp, orderBy, query, getDoc, onSnapshot
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FiPlus, FiEdit2, FiTrash2, FiX, FiUpload, FiPackage, FiBook, FiSearch } from "react-icons/fi";
import { bundleDeliveryOf } from "../../utils/bundles";
import { compressImage } from "../../utils/imageUtils";
import toast from "react-hot-toast";

const quillModules = {
    toolbar: [['bold', 'italic', 'underline'], [{ 'align': [] }], ['clean']],
};

const EMPTY_FORM = {
    title: "", author: "", category: "",
    series: "", bookNumber: "",
    language: "English",
    description: "", imageUrl: "",
    isFeatured: false,
    productType: "regular",
    isPreorder: false,
    releaseDate: "",
    seoTitle: "",
    seoDescription: "",
    seoKeywords: "",
    metaContentId: "",
    // Variants array — each variant drives its own price/stock/format
    variants: [],
    // Legacy / fallback fields (auto-set from variants on save)
    format: "Paperback",
    price: "",
    originalPrice: "",
    stock: "",
    isDigital: false,
    epubStoragePath: "",
    epubFileName: "",
    bundleItems: [],
    bundleFormat: "Physical",
};

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [uploading, setUploading] = useState(false);
    // per-variant epub upload tracking: { [variantIdx]: { uploading, progress } }
    const [variantEpubState, setVariantEpubState] = useState({});
    const [saving, setSaving] = useState(false);
    const [isNewSeries, setIsNewSeries] = useState(false);
    const [bookSearch, setBookSearch] = useState("");
    const [bundleVariantIdx, setBundleVariantIdx] = useState(null); // which variant's bundle picker is open
    const [categories, setCategories] = useState([]);
    const [digitalCategory, setDigitalCategory] = useState("");
    const [generalConfig, setGeneralConfig] = useState(null);

    const uniqueSeries = Array.from(new Set(products.map(p => p.series).filter(Boolean))).sort();
    // Only regular products can be added to bundles
    const regularProducts = products.filter(p => p.productType !== "bundle");
    const searchedBooks = bookSearch.trim()
        ? regularProducts.filter(p => p.title.toLowerCase().includes(bookSearch.toLowerCase()))
        : regularProducts;

    async function loadProducts() {
        setLoading(true);
        try {
            const snap = await getDocs(query(collection(db, "products"), orderBy("createdAt", "desc")));
            setProducts(snap.docs.map(d => ({ ...d.data(), id: d.id })));
        } catch (e) {
            toast.error("Could not load products.");
        }
        setLoading(false);
    }

    useEffect(() => { 
        loadProducts(); 
        
        // Listen to site config for real-time category updates and defaults
        const unsub = onSnapshot(doc(db, "siteConfig", "general"), (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                setGeneralConfig(data);
                setCategories(data.categories || []);
                setDigitalCategory(data.digitalCategory || "");
            }
        });

        return () => unsub();
    }, []);

    // Open the editor at the top of the page
    useEffect(() => {
        if (modal) window.scrollTo({ top: 0 });
    }, [modal]);

    function openAdd() { 
        setForm({ ...EMPTY_FORM, author: generalConfig?.authorName || "Liliz Black" }); 
        setEditId(null); 
        setModal(true); 
        setBookSearch(""); 
    }
    function openEdit(p) {
        setForm({
            title: p.title || "", author: p.author || generalConfig?.authorName || "Liliz Black",
            category: p.category || "",
            language: p.language || "English",
            description: p.description || "",
            imageUrl: p.imageUrl || "",
            isFeatured: p.isFeatured || false,
            productType: p.productType || "regular",
            isPreorder: p.isPreorder || false,
            releaseDate: p.releaseDate || "",
            seoTitle: p.seoTitle || "",
            seoDescription: p.seoDescription || "",
            seoKeywords: p.seoKeywords || "",
            metaContentId: p.metaContentId || "",
            // Variants — if product has them use them, else synthesize from legacy fields
            variants: p.variants && p.variants.length > 0
                ? p.variants
                : [{
                    format: p.isDigital ? "eBook" : (p.format || "Paperback"),
                    price: p.price || "",
                    originalPrice: p.originalPrice || "",
                    stock: p.stock ?? "",
                    isDigital: p.isDigital || false,
                    epubStoragePath: p.epubStoragePath || "",
                    epubFileName: p.epubFileName || "",
                    bundleItems: p.bundleItems || [],
                }],
            // Legacy fallback (will be overwritten on save)
            format: p.isDigital ? "eBook" : (p.format || "Paperback"),
            price: p.price || "",
            originalPrice: p.originalPrice || "",
            stock: p.stock ?? "",
            isDigital: p.isDigital || false,
            epubStoragePath: p.epubStoragePath || "",
            epubFileName: p.epubFileName || "",
            bundleItems: p.bundleItems || [],
            bundleFormat: p.bundleFormat || "Physical",
            series: p.series || "",
            bookNumber: p.bookNumber || "",
        });
        setIsNewSeries(false);
        setBookSearch("");
        setBundleVariantIdx(null);
        setVariantEpubState({});
        setEditId(p.id);
        setModal(true);
    }
    function closeModal() { setModal(false); setEditId(null); setBundleVariantIdx(null); setVariantEpubState({}); }

    function handleChange(e) {
        const { name, value, type, checked } = e.target;
        if (name === "series" && value === "__NEW__") {
            setIsNewSeries(true);
            setForm(f => ({ ...f, series: "" }));
            return;
        }
        setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    }

    function handleDescriptionChange(content) {
        setForm(f => ({ ...f, description: content }));
    }

    // ── Variant management ─────────────────────────────────
    const FORMAT_OPTIONS = ["Paperback", "Hardcover", "eBook", "Deluxe Edition", "Bundle"];

    function addVariant() {
        setForm(f => ({
            ...f,
            variants: [...f.variants, {
                format: "Paperback",
                price: "",
                originalPrice: "",
                stock: "",
                isDigital: false,
                epubStoragePath: "",
                epubFileName: "",
                bundleItems: [],
            }]
        }));
    }

    function updateVariant(idx, field, value) {
        setForm(f => {
            const variants = [...f.variants];
            variants[idx] = { ...variants[idx], [field]: value };
            if (field === "format") {
                variants[idx].isDigital = value === "eBook";
                if (value !== "eBook") { variants[idx].epubStoragePath = ""; variants[idx].epubFileName = ""; }
                if (value !== "Bundle") { variants[idx].bundleItems = []; }
            }
            return { ...f, variants };
        });
    }

    function removeVariant(idx) {
        setForm(f => ({
            ...f,
            variants: f.variants.filter((_, i) => i !== idx)
        }));
        setVariantEpubState(s => { const n = { ...s }; delete n[idx]; return n; });
    }

    // Legacy bundle products (productType "bundle") keep their books in form.bundleItems
    function isBundleItemSelected(productId) {
        return (form.bundleItems || []).some(b => b.id === productId);
    }

    function toggleBundleItem(product) {
        setForm(f => {
            const items = f.bundleItems || [];
            const exists = items.some(b => b.id === product.id);
            return {
                ...f,
                bundleItems: exists
                    ? items.filter(b => b.id !== product.id)
                    : [...items, { id: product.id, title: product.title, author: product.author, imageUrl: product.imageUrl || "" }],
            };
        });
    }

    // Toggle bundle item for a specific variant
    function toggleVariantBundleItem(variantIdx, product) {
        setForm(f => {
            const variants = [...f.variants];
            const items = variants[variantIdx].bundleItems || [];
            const exists = items.some(b => b.id === product.id);
            
            // Find the eBook variant of the product to get the correct storage path and digital flag
            const ebookVariant = (product.variants || []).find(v => v.format === "eBook");
            const isDigital = ebookVariant ? true : (product.isDigital || false);
            const epubStoragePath = ebookVariant?.epubStoragePath || product.epubStoragePath || null;

            variants[variantIdx] = {
                ...variants[variantIdx],
                bundleItems: exists
                    ? items.filter(b => b.id !== product.id)
                    : [...items, {
                        id: product.id, title: product.title,
                        author: product.author, imageUrl: product.imageUrl || "",
                        description: product.description || "",
                        isDigital: isDigital,
                        epubStoragePath: epubStoragePath,
                    }]
            };
            return { ...f, variants };
        });
    }

    async function handleImageUpload(e) {
        const rawFile = e.target.files[0];
        if (!rawFile) return;
        setUploading(true);
        try {
            const file = await compressImage(rawFile);
            const storageRef = ref(storage, `products/${Date.now()}_${file.name}`);
            const task = uploadBytesResumable(storageRef, file);
            task.on("state_changed", null,
                () => { toast.error("Upload failed."); setUploading(false); },
                async () => {
                    const url = await getDownloadURL(task.snapshot.ref);
                    setForm(f => ({ ...f, imageUrl: url }));
                    setUploading(false);
                    toast.success("Image uploaded!");
                }
            );
        } catch (err) {
            toast.error("Error processing image.");
            setUploading(false);
        }
    }

    async function handleBundleImageUpload(e, variantIdx) {
        const rawFile = e.target.files[0];
        if (!rawFile) return;
        setUploading(true);
        try {
            const file = await compressImage(rawFile, 1600, true);
            const storageRef = ref(storage, `products/bundles/${Date.now()}_${file.name}`);
            const task = uploadBytesResumable(storageRef, file);
            task.on("state_changed", null,
                () => { toast.error("Upload failed."); setUploading(false); },
                async () => {
                    const url = await getDownloadURL(task.snapshot.ref);
                    updateVariant(variantIdx, "bundleImageUrl", url);
                    setUploading(false);
                    toast.success("Bundle picture uploaded!");
                }
            );
        } catch (err) {
            toast.error("Error processing image.");
            setUploading(false);
        }
    }

    async function handleEpubUploadForVariant(e, variantIdx) {
        const file = e.target.files[0];
        if (!file) return;
        const ext = file.name.split(".").pop().toLowerCase();
        if (!["epub", "pdf", "mobi"].includes(ext)) {
            toast.error("Please upload an EPUB, PDF, or MOBI file.");
            return;
        }
        setVariantEpubState(s => ({ ...s, [variantIdx]: { uploading: true, progress: 0 } }));
        try {
            const bookKey = editId || `new_${Date.now()}`;
            const storagePath = `digital-books/${bookKey}/v${variantIdx}_${file.name}`;
            const storageRef = ref(storage, storagePath);
            const task = uploadBytesResumable(storageRef, file);
            task.on(
                "state_changed",
                snap => setVariantEpubState(s => ({ ...s, [variantIdx]: { uploading: true, progress: Math.round((snap.bytesTransferred / snap.totalBytes) * 100) } })),
                () => { toast.error("EPUB upload failed."); setVariantEpubState(s => ({ ...s, [variantIdx]: { uploading: false, progress: 0 } })); },
                async () => {
                    updateVariant(variantIdx, "epubStoragePath", storagePath);
                    updateVariant(variantIdx, "epubFileName", file.name);
                    setVariantEpubState(s => ({ ...s, [variantIdx]: { uploading: false, progress: 100 } }));
                    toast.success("EPUB uploaded!");
                }
            );
        } catch {
            toast.error("Error uploading EPUB.");
            setVariantEpubState(s => ({ ...s, [variantIdx]: { uploading: false, progress: 0 } }));
        }
    }

    async function handleSave() {
        if (!form.title || !form.variants.length) {
            toast.error("Title and at least one variant (format/price) are required.");
            return;
        }
        if (form.variants.some(v => !v.price)) {
            toast.error("All variants must have a price.");
            return;
        }
        setSaving(true);
        try {
            // Derive legacy top-level fields from first variant for backward-compat
            const firstVariant = (form.variants || [])[0] || {};
            const anyDigital = (form.variants || []).some(v => v.isDigital || v.format === "eBook");

            const payload = {
                ...form,
                language: form.language || "English",
                variants: (form.variants || []).map(v => ({
                    ...v,
                    price: parseFloat(v.price) || 0,
                    originalPrice: v.originalPrice ? parseFloat(v.originalPrice) : null,
                    // Firestore rejects undefined values, so only set this for bundle options
                    ...(v.format === "Bundle" ? { bundleDelivery: bundleDeliveryOf(v) } : {}),
                    stock: (v.format === "eBook" || v.format === "Bundle") ? null : parseInt(v.stock || 0),
                })),
                // Legacy fields derived from first variant for older components
                format: firstVariant.format || "Paperback",
                price: parseFloat(firstVariant.price) || 0,
                originalPrice: firstVariant.originalPrice ? parseFloat(firstVariant.originalPrice) : null,
                stock: (firstVariant.isDigital || firstVariant.format === "eBook" || firstVariant.format === "Bundle") ? null : parseInt(firstVariant.stock || 0),
                isDigital: !!anyDigital,
                epubStoragePath: firstVariant.epubStoragePath || "",
                epubFileName: firstVariant.epubFileName || "",
                bundleItems: (form.variants || []).find(v => v.format === "Bundle")?.bundleItems || [],
                productType: "regular",
                updatedAt: serverTimestamp()
            };
            
            if (editId) {
                console.log("Updating product:", editId);
                await updateDoc(doc(db, "products", editId), payload);
                toast.success("Product updated!");
            } else {
                console.log("Adding new product");
                await addDoc(collection(db, "products"), { ...payload, createdAt: serverTimestamp() });
                toast.success("Product added!");
            }
            closeModal();
            loadProducts();
        } catch (e) {
            console.error("Save error:", e);
            toast.error("Failed to save product.");
        }
        setSaving(false);
    }

    async function handleDelete(id) {
        console.log("Delete button clicked for ID:", id);
        if (!id) {
            toast.error("Invalid product ID. Cannot delete.");
            return;
        }
        if (!window.confirm("Are you sure you want to delete this product? This action cannot be undone.")) return;
        try {
            console.log("Deleting document: products/", id);
            await deleteDoc(doc(db, "products", id));
            toast.success("Product deleted successfully.");
            await loadProducts();
        } catch (e) {
            console.error("Error deleting product:", e);
            toast.error("Failed to delete product: " + e.message);
        }
    }

    const bundles = products.filter(p => p.variants && p.variants.some(v => v.format === "Bundle"));
    const regular = products.filter(p => !p.variants || !p.variants.some(v => v.format === "Bundle"));

    return (
        <>
            {!modal && (
            <>
            <div className="admin-stats">
                <div className="admin-stat-card">
                    <div className="stat-label">Total Products</div>
                    <div className="stat-value">{products.length}</div>
                </div>
                <div className="admin-stat-card">
                    <div className="stat-label">Regular Books</div>
                    <div className="stat-value">{regular.length}</div>
                </div>
                <div className="admin-stat-card">
                    <div className="stat-label">Bundles</div>
                    <div className="stat-value">{bundles.length}</div>
                </div>
                <div className="admin-stat-card">
                    <div className="stat-label">Featured</div>
                    <div className="stat-value">{products.filter(p => p.isFeatured).length}</div>
                </div>
            </div>

            <div className="admin-section-header">
                <h3>All Products & Bundles</h3>
                <div className="admin-action-row">
                    <button className="admin-btn-primary" onClick={openAdd}>
                        <FiPlus size={16} /> Add Product
                    </button>
                </div>
            </div>

            <div className="admin-card">
                {loading ? (
                    <div className="admin-loading">Loading products…</div>
                ) : products.length === 0 ? (
                    <div className="admin-empty">
                        <FiPlus size={40} />
                        <p>No products yet. Add your first book!</p>
                    </div>
                ) : (
                    <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Cover</th>
                                <th>Title</th>
                                <th>Type</th>
                                <th>Category</th>
                                <th>Price</th>
                                <th>Stock</th>
                                <th>Featured</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map(p => (
                                <tr key={p.id}>
                                    <td>
                                        {p.imageUrl
                                            ? <img src={p.imageUrl} alt={p.title} className="admin-img-preview" />
                                            : <div style={{ width: 56, height: 72, background: "#f0f0f0", borderRadius: 6 }} />
                                        }
                                    </td>
                                    <td>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <strong>{p.title}</strong>
                                            {p.language === "Spanish" ? <span title="Spanish">🇵🇷</span> : <span title="English">🇺🇸</span>}
                                        </div>
                                        <div style={{ color: "#888", fontSize: 12 }}>
                                            {p.author}
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                            {(p.variants || []).length > 0 ? (
                                                p.variants.map((v, i) => (
                                                    <span key={i} className={`status-badge ${v.format === "eBook" ? "digital" : v.format === "Bundle" ? "bundle" : "physical"}`} style={{ fontSize: 10, padding: "2px 6px" }}>
                                                        {v.format === "eBook" ? "📱" : v.format === "Bundle" ? "📦" : "📖"} {v.format}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="status-badge physical" style={{ fontSize: 10, padding: "2px 6px" }}>
                                                    {p.format || "Physical"}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>{p.category}</td>
                                    <td>
                                        {(() => {
                                            const prices = (p.variants || []).map(v => parseFloat(v.price)).filter(p => !isNaN(p));
                                            if (prices.length === 0) return `$${parseFloat(p.price || 0).toFixed(2)}`;
                                            const minPrice = Math.min(...prices);
                                            const maxPrice = Math.max(...prices);
                                            return minPrice === maxPrice 
                                                ? `$${minPrice.toFixed(2)}`
                                                : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
                                        })()}
                                    </td>
                                    <td>
                                        {(() => {
                                            if ((p.variants || []).length === 0) return p.isDigital ? "—" : (p.stock ?? 0);
                                            const totalStock = p.variants.reduce((acc, v) => acc + (v.format !== "eBook" ? (parseInt(v.stock) || 0) : 0), 0);
                                            const hasPhysical = p.variants.some(v => v.format !== "eBook");
                                            return hasPhysical ? totalStock : "—";
                                        })()}
                                    </td>
                                    <td>{p.isFeatured ? "⭐" : "—"}</td>
                                    <td>
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <button type="button" className="admin-btn-secondary" onClick={() => openEdit(p)} style={{ padding: "6px 12px" }}>
                                                <FiEdit2 size={14} />
                                            </button>
                                            <button type="button" className="admin-btn-danger" onClick={() => handleDelete(p.id)} style={{ padding: "6px 12px" }}>
                                                <FiTrash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </div>

            </>
            )}

            {/* ── Product editor (full page) ─────────────── */}
            {modal && (
                <div className="admin-product-page">
                    <div className="admin-product-page-inner">
                        <div className="admin-product-page-header">
                            <button className="admin-btn-secondary" onClick={closeModal}>← Back to products</button>
                            <h3>{editId ? "Edit Product" : "Add New Product"}</h3>
                            <span />
                        </div>
                        <div className="admin-product-page-body">

                            {/* ── Language + Series row ── */}
                            <div className="admin-flex-row" style={{ marginBottom: 20 }}>
                                <div style={{ flex: "0 0 200px", minWidth: "100%" }}>
                                    <label style={{ fontSize: 12, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 6 }}>🌐 Language</label>
                                    <select
                                        name="language"
                                        value={form.language}
                                        onChange={handleChange}
                                        style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1.5px solid #e0ddd8", fontWeight: 600, fontSize: 14 }}
                                    >
                                        <option value="English">🇺🇸 English</option>
                                        <option value="Spanish">🇵🇷 Spanish</option>
                                    </select>
                                </div>
                            </div>

                            <div className="admin-form">
                                <div className="admin-form-grid">
                                    {/* Common fields */}
                                    <div className="admin-form-field full">
                                        <label>{form.productType === "bundle" ? "Bundle Name *" : "Book Title *"}</label>
                                        <input name="title" value={form.title} onChange={handleChange}
                                            placeholder={form.productType === "bundle" ? "Dark Vows Complete Series" : "Dark Vows"} />
                                    </div>
                                    <div className="admin-form-field">
                                        <label>Author</label>
                                        <input name="author" value={form.author} onChange={handleChange} />
                                    </div>
                                    <div className="admin-form-field">
                                        <label>Category</label>
                                        <select name="category" value={form.category} onChange={handleChange}>
                                            <option value="">Select a Category</option>
                                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>

                                    {/* ── Metadata fields ── */}
                                    <div className="admin-form-field">
                                        <label>Series</label>
                                        {isNewSeries ? (
                                            <div style={{ display: "flex", gap: 8 }}>
                                                <input name="series" value={form.series} onChange={handleChange} placeholder="New series name" autoFocus />
                                                <button type="button" className="admin-btn-secondary" onClick={() => setIsNewSeries(false)} style={{ padding: "0 12px" }}>Back</button>
                                            </div>
                                        ) : (
                                            <select name="series" value={form.series} onChange={handleChange}>
                                                <option value="">None / Standalone</option>
                                                {uniqueSeries.map(s => <option key={s} value={s}>{s}</option>)}
                                                <option value="__NEW__" style={{ fontWeight: "bold", color: "var(--accent)" }}>+ Create New Series...</option>
                                            </select>
                                        )}
                                    </div>
                                    <div className="admin-form-field">
                                        <label>Book # in Series</label>
                                        <input name="bookNumber" type="number" min="1" value={form.bookNumber} onChange={handleChange} placeholder="e.g. 1" disabled={!form.series} />
                                    </div>

                                    {/* ── VARIANTS EDITOR ── */}
                                    <div className="admin-form-field full" style={{ marginTop: 12 }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                                            <label style={{ fontSize: 14, fontWeight: 700, color: "var(--primary)", margin: 0 }}>
                                                📦 Formats &amp; Editions (Variants)
                                            </label>
                                            <button
                                                type="button"
                                                onClick={addVariant}
                                                className="admin-btn-secondary"
                                                style={{ padding: "6px 16px", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}
                                            >
                                                <FiPlus size={14} /> Add Format
                                            </button>
                                        </div>

                                        {form.variants.length === 0 && (
                                            <div style={{ textAlign: "center", padding: "24px 0", color: "#aaa", border: "2px dashed #e0ddd8", borderRadius: 12 }}>
                                                <p style={{ margin: 0, fontWeight: 600 }}>No formats yet.</p>
                                                <p style={{ margin: "4px 0 0", fontSize: 13 }}>Click "Add Format" to add Paperback, eBook, Deluxe Edition, or Bundle options.</p>
                                            </div>
                                        )}

                                        {form.variants.map((v, idx) => {
                                            const isVariantEbook = v.format === "eBook";
                                            const isVariantBundle = v.format === "Bundle";
                                            const epubState = variantEpubState[idx] || { uploading: false, progress: 0 };
                                            return (
                                                <div key={idx} style={{
                                                    border: "1.5px solid #e0ddd8", borderRadius: 14, padding: 18,
                                                    marginBottom: 14, background: "#fdfdfc"
                                                }}>
                                                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                                                        {/* Format selector */}
                                                        <div style={{ flex: "1 1 160px" }}>
                                                            <label style={{ fontSize: 12, fontWeight: 600, color: "#888", display: "block", marginBottom: 4 }}>Format</label>
                                                            <select
                                                                value={v.format}
                                                                onChange={e => updateVariant(idx, "format", e.target.value)}
                                                                style={{ width: "100%" }}
                                                            >
                                                                {FORMAT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                                                            </select>
                                                        </div>
                                                        {/* Price */}
                                                        <div style={{ flex: "1 1 120px" }}>
                                                            <label style={{ fontSize: 12, fontWeight: 600, color: "#888", display: "block", marginBottom: 4 }}>Price *</label>
                                                            <input
                                                                type="number" step="0.01" min="0"
                                                                value={v.price} placeholder="19.99"
                                                                onChange={e => updateVariant(idx, "price", e.target.value)}
                                                            />
                                                        </div>
                                                        {/* Original price */}
                                                        <div style={{ flex: "1 1 120px" }}>
                                                            <label style={{ fontSize: 12, fontWeight: 600, color: "#888", display: "block", marginBottom: 4 }}>Original Price</label>
                                                            <input
                                                                type="number" step="0.01" min="0"
                                                                value={v.originalPrice || ""} placeholder="29.99"
                                                                onChange={e => updateVariant(idx, "originalPrice", e.target.value)}
                                                            />
                                                        </div>
                                                        {/* Stock (physical only) */}
                                                        {(!isVariantEbook && !isVariantBundle) && (
                                                            <div style={{ flex: "1 1 100px" }}>
                                                                <label style={{ fontSize: 12, fontWeight: 600, color: "#888", display: "block", marginBottom: 4 }}>Stock</label>
                                                                <input
                                                                    type="number" min="0"
                                                                    value={v.stock || ""} placeholder="50"
                                                                    onChange={e => updateVariant(idx, "stock", e.target.value)}
                                                                />
                                                            </div>
                                                        )}
                                                        {/* Remove button */}
                                                        <button
                                                            type="button"
                                                            onClick={() => removeVariant(idx)}
                                                            className="admin-btn-danger"
                                                            style={{ padding: "8px 12px", alignSelf: "flex-end" }}
                                                            title="Remove this variant"
                                                        >
                                                            <FiTrash2 size={14} />
                                                        </button>
                                                    </div>

                                                    {/* eBook: EPUB upload */}
                                                    {isVariantEbook && (
                                                        <div style={{ marginTop: 14, padding: 14, background: "#f0f7ff", borderRadius: 10, border: "1.5px dashed #90caf9" }}>
                                                            <label style={{ fontSize: 13, fontWeight: 700, color: "#1565c0", display: "block", marginBottom: 8 }}>📖 EPUB / PDF File</label>
                                                            {v.epubFileName && (
                                                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, background: "#e8f5e9", padding: "8px 12px", borderRadius: 8 }}>
                                                                    <span>📗</span>
                                                                    <span style={{ fontSize: 13, color: "#2e7d32", flex: 1 }}>{v.epubFileName}</span>
                                                                    <button type="button" onClick={() => { updateVariant(idx, "epubStoragePath", ""); updateVariant(idx, "epubFileName", ""); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#e53935" }}>✕</button>
                                                                </div>
                                                            )}
                                                            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", background: "#fff", border: "1.5px solid #90caf9", padding: "8px 16px", borderRadius: 8, fontWeight: 600, fontSize: 13, color: "#1565c0" }}>
                                                                <FiUpload size={14} />
                                                                {epubState.uploading ? `Uploading… ${epubState.progress}%` : v.epubFileName ? "Replace File" : "Upload EPUB / PDF"}
                                                                <input type="file" accept=".epub,.pdf,.mobi" onChange={e => handleEpubUploadForVariant(e, idx)} style={{ display: "none" }} disabled={epubState.uploading} />
                                                            </label>
                                                            {epubState.uploading && (
                                                                <div style={{ marginTop: 8, height: 5, background: "#e3f2fd", borderRadius: 4, overflow: "hidden" }}>
                                                                    <div style={{ height: "100%", width: `${epubState.progress}%`, background: "#1565c0", transition: "width 0.3s" }} />
                                                                </div>
                                                            )}
                                                            {!v.epubStoragePath && !epubState.uploading && (
                                                                <p style={{ fontSize: 12, color: "#e57373", margin: "6px 0 0" }}>⚠ No file yet. Customers can't download until uploaded.</p>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Bundle: Settings */}
                                                    {isVariantBundle && (
                                                        <div style={{ marginTop: 14, padding: 14, background: "#f5f3ff", borderRadius: 10, border: "1.5px solid #ddd6fe" }}>
                                                            <div style={{ marginBottom: 14 }}>
                                                                <label style={{ fontSize: 12, fontWeight: 700, color: "#7c3aed", display: "block", marginBottom: 6 }}>🏷️ Bundle Name</label>
                                                                <input value={v.bundleName || ""} onChange={e => updateVariant(idx, "bundleName", e.target.value)} placeholder="Liliz Black Starter Pack" />
                                                            </div>
                                                            <div style={{ marginBottom: 14 }}>
                                                                <label style={{ fontSize: 12, fontWeight: 700, color: "#7c3aed", display: "block", marginBottom: 6 }}>📝 Short Description (what the customer gets)</label>
                                                                <textarea value={v.bundleDescription || ""} maxLength={300} rows={3} onChange={e => updateVariant(idx, "bundleDescription", e.target.value)} placeholder="Physical copies of Under the Sky and Through the Dusk, shipped to your door." style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #e0ddd8", fontFamily: "inherit", boxSizing: "border-box" }} />
                                                                <div style={{ fontSize: 11, color: "#888", textAlign: "right" }}>{(v.bundleDescription || "").length}/300</div>
                                                            </div>
                                                            <div style={{ marginBottom: 14 }}>
                                                                <label style={{ fontSize: 12, fontWeight: 700, color: "#7c3aed", display: "block", marginBottom: 6 }}>🖼️ Bundle Picture (shows what's inside)</label>
                                                                {v.bundleImageUrl && <img src={v.bundleImageUrl} alt="Bundle contents" style={{ height: 120, objectFit: "cover", borderRadius: 8, marginBottom: 8, display: "block" }} />}
                                                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                                    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", background: "#fff", border: "1.5px dashed #c4b5fd", padding: "8px 14px", borderRadius: 8, fontWeight: 600, fontSize: 13, color: "#7c3aed" }}>
                                                                        {uploading ? "Uploading…" : (v.bundleImageUrl ? "Replace picture" : "Upload picture")}
                                                                        <input type="file" accept="image/*" onChange={e => handleBundleImageUpload(e, idx)} style={{ display: "none" }} />
                                                                    </label>
                                                                    {v.bundleImageUrl && (
                                                                        <button type="button" onClick={() => updateVariant(idx, "bundleImageUrl", "")} style={{ background: "none", border: "none", color: "#dc3545", cursor: "pointer", fontSize: 13 }}>Remove</button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <p style={{ margin: "0 0 12px", fontSize: 12, color: "#6d28d9" }}>
                                                                Bundles have no stock of their own. Each sale takes copies from the books inside the bundle. Bundles with "Reader" in the name are always Physical + Digital.
                                                            </p>
                                                            <div style={{ marginBottom: 14 }}>
                                                                <label style={{ fontSize: 12, fontWeight: 700, color: "#7c3aed", display: "block", marginBottom: 6 }}>📦 Bundle Delivery Type</label>
                                                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => updateVariant(idx, "bundleDelivery", "Physical")}
                                                                        className={(bundleDeliveryOf(v) || "Physical") === "Physical" ? "admin-btn-primary" : "admin-btn-secondary"}
                                                                        style={{ flex: "1 1 150px", padding: "8px", fontSize: 13, border: (bundleDeliveryOf(v) || "Physical") === "Physical" ? "none" : "1.5px solid #ddd" }}
                                                                    >
                                                                        📦 Physical (Ship copies)
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => updateVariant(idx, "bundleDelivery", "Digital")}
                                                                        className={bundleDeliveryOf(v) === "Digital" ? "admin-btn-primary" : "admin-btn-secondary"}
                                                                        style={{ flex: "1 1 150px", padding: "8px", fontSize: 13, border: bundleDeliveryOf(v) === "Digital" ? "none" : "1.5px solid #ddd" }}
                                                                    >
                                                                        📱 Digital (Email links)
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => updateVariant(idx, "bundleDelivery", "Combo")}
                                                                        className={bundleDeliveryOf(v) === "Combo" ? "admin-btn-primary" : "admin-btn-secondary"}
                                                                        style={{ flex: "1 1 150px", padding: "8px", fontSize: 13, border: bundleDeliveryOf(v) === "Combo" ? "none" : "1.5px solid #ddd" }}
                                                                    >
                                                                        🎁 Combo (Physical + Digital)
                                                                    </button>
                                                                </div>
                                                                {bundleDeliveryOf(v) === "Combo" && (
                                                                    <p style={{ margin: "8px 0 0", fontSize: 12, color: "#7c3aed" }}>
                                                                        Customers pay shipping for the physical copies and also receive download links for the digital books.
                                                                    </p>
                                                                )}
                                                            </div>

                                                            <div style={{ marginTop: 14 }}>
                                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                                                <label style={{ fontSize: 13, fontWeight: 700, color: "#7c3aed", margin: 0 }}>
                                                                    📦 Bundle Books ({(v.bundleItems || []).length} selected)
                                                                </label>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setBundleVariantIdx(bundleVariantIdx === idx ? null : idx)}
                                                                    className="admin-btn-secondary"
                                                                    style={{ padding: "4px 12px", fontSize: 12 }}
                                                                >
                                                                    {bundleVariantIdx === idx ? "Close Picker" : "Pick Books"}
                                                                </button>
                                                            </div>
                                                            {/* Selected chips */}
                                                            {(v.bundleItems || []).length > 0 && (
                                                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                                                                    {v.bundleItems.map((bi, bi_idx) => (
                                                                        <div key={bi_idx} style={{ background: "#f0e6ff", border: "1px solid #d8b4fe", padding: "4px 10px", borderRadius: 16, fontSize: 12, fontWeight: 600, color: "#7c3aed", display: "flex", alignItems: "center", gap: 6 }}>
                                                                            {bi.title}
                                                                            <button type="button" onClick={() => toggleVariantBundleItem(idx, bi)} style={{ background: "none", border: "none", cursor: "pointer", color: "#7c3aed", padding: 0 }}><FiX size={11} /></button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {bundleVariantIdx === idx && (
                                                                <div style={{ border: "1.5px solid #e0ddd8", borderRadius: 10, padding: 8 }}>
                                                                    <div style={{ position: "relative", marginBottom: 8 }}>
                                                                        <FiSearch size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#aaa" }} />
                                                                        <input value={bookSearch} onChange={e => setBookSearch(e.target.value)} placeholder="Search books…" style={{ paddingLeft: 32 }} />
                                                                    </div>
                                                                    <div style={{ maxHeight: 200, overflowY: "auto" }}>
                                                                        {searchedBooks.map(book => {
                                                                            const sel = (v.bundleItems || []).some(b => b.id === book.id);
                                                                            return (
                                                                                <div key={book.id} onClick={() => toggleVariantBundleItem(idx, book)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: sel ? "#f0e6ff" : "transparent", marginBottom: 2 }}>
                                                                                    {book.imageUrl && <img src={book.imageUrl} alt={book.title} style={{ width: 28, height: 36, objectFit: "cover", borderRadius: 3 }} />}
                                                                                    <div style={{ flex: 1 }}>
                                                                                        <div style={{ fontWeight: 600, fontSize: 13, color: sel ? "#7c3aed" : "#333" }}>{book.title}</div>
                                                                                        <div style={{ fontSize: 11, color: "#888" }}>{book.author}</div>
                                                                                    </div>
                                                                                    {sel && <span style={{ color: "#7c3aed", fontWeight: 800 }}>✓</span>}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="admin-form-field full">
                                        <label>{form.productType === "bundle" ? "Bundle Description" : "Description"}</label>
                                        <ReactQuill
                                            theme="snow"
                                            value={form.description}
                                            onChange={handleDescriptionChange}
                                            modules={quillModules}
                                            style={{ height: 180, marginBottom: 50 }}
                                        />
                                    </div>

                                    <div className="admin-form-field full">
                                        <label>{form.productType === "bundle" ? "Bundle Cover Image" : "Cover Image"}</label>
                                        {form.imageUrl && (
                                            <img src={form.imageUrl} alt="Preview" style={{ height: 90, objectFit: "cover", borderRadius: 8, marginBottom: 8 }} />
                                        )}
                                        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", background: "#f4f5f7", border: "1.5px dashed #ccc", padding: "10px 16px", borderRadius: 8, fontWeight: 600, fontSize: 13, color: "#555" }}>
                                            <FiUpload size={16} />
                                            {uploading ? "Uploading…" : "Upload Image"}
                                            <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: "none" }} />
                                        </label>
                                        <span style={{ fontSize: 12, color: "#888", marginTop: 4 }}>Or paste URL below:</span>
                                        <input name="imageUrl" value={form.imageUrl} onChange={handleChange} placeholder="https://..." />
                                    </div>

                                    {/* Bundle: Book Picker */}
                                    {form.productType === "bundle" && (
                                        <div className="admin-form-field full">
                                            <label>Select Books to Include in Bundle *
                                                <span style={{ fontWeight: 400, color: "#888", marginLeft: 8 }}>
                                                    ({form.bundleItems.length} selected — min 2)
                                                </span>
                                            </label>

                                            {/* Selected books chips */}
                                            {form.bundleItems.length > 0 && (
                                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                                                    {form.bundleItems.map((item, i) => (
                                                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: "#f0e6ff", border: "1px solid #d8b4fe", padding: "6px 12px", borderRadius: 20, fontSize: 13, fontWeight: 600, color: "#7c3aed" }}>
                                                            {item.title}
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleBundleItem(item)}
                                                                style={{ background: "none", border: "none", cursor: "pointer", color: "#7c3aed", padding: 0, lineHeight: 1 }}
                                                            >
                                                                <FiX size={13} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Search */}
                                            <div style={{ position: "relative", marginBottom: 8 }}>
                                                <FiSearch size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#aaa" }} />
                                                <input
                                                    value={bookSearch}
                                                    onChange={e => setBookSearch(e.target.value)}
                                                    placeholder="Search books to add…"
                                                    style={{ paddingLeft: 36 }}
                                                />
                                            </div>

                                            {/* Book picker list */}
                                            <div style={{ maxHeight: 240, overflowY: "auto", border: "1.5px solid #e0ddd8", borderRadius: 10, padding: 8 }}>
                                                {searchedBooks.length === 0 && (
                                                    <p style={{ color: "#aaa", fontSize: 13, textAlign: "center", padding: "20px 0" }}>No regular products found.</p>
                                                )}
                                                {searchedBooks.map(book => {
                                                    const selected = isBundleItemSelected(book.id);
                                                    return (
                                                        <div
                                                            key={book.id}
                                                            onClick={() => toggleBundleItem(book)}
                                                            style={{
                                                                display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
                                                                borderRadius: 8, cursor: "pointer", marginBottom: 4,
                                                                background: selected ? "#f0e6ff" : "transparent",
                                                                border: selected ? "1.5px solid #d8b4fe" : "1.5px solid transparent",
                                                                transition: "all 0.15s"
                                                            }}
                                                        >
                                                            {book.imageUrl
                                                                ? <img src={book.imageUrl} alt={book.title} style={{ width: 36, height: 46, objectFit: "cover", borderRadius: 4 }} />
                                                                : <div style={{ width: 36, height: 46, background: "#eee", borderRadius: 4 }} />
                                                            }
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontWeight: 600, fontSize: 14, color: selected ? "#7c3aed" : "#333" }}>{book.title}</div>
                                                                <div style={{ fontSize: 12, color: "#888" }}>{book.author} · ${parseFloat(book.price).toFixed(2)}</div>
                                                            </div>
                                                            {selected && <span style={{ color: "#7c3aed", fontWeight: 700, fontSize: 18 }}>✓</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}




                                    <div className="admin-form-field" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                                        <input type="checkbox" name="isFeatured" id="isFeatured" checked={form.isFeatured} onChange={handleChange} style={{ width: 16, height: 16 }} />
                                        <label htmlFor="isFeatured" style={{ cursor: "pointer" }}>Featured on Homepage</label>
                                    </div>

                                    <div className="admin-form-field" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                                        <input type="checkbox" name="isPreorder" id="isPreorder" checked={form.isPreorder} onChange={handleChange} style={{ width: 16, height: 16 }} />
                                        <label htmlFor="isPreorder" style={{ cursor: "pointer" }}>Pre-order Item</label>
                                    </div>

                                    {form.isPreorder && (
                                        <div className="admin-form-field full" style={{ marginTop: -10 }}>
                                            <label>Release Date</label>
                                            <input 
                                                type="date" 
                                                name="releaseDate" 
                                                value={form.releaseDate} 
                                                onChange={handleChange} 
                                                required={form.isPreorder}
                                                style={{ padding: "10px 14px" }}
                                            />
                                            <p style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                                                Customers will be able to buy this item even if stock is 0. 
                                                The release date will be shown on the product page.
                                            </p>
                                        </div>
                                    )}

                                    {/* ── SEO Section ── */}
                                    <div className="admin-form-field full" style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #eee" }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "1.1rem", color: "var(--primary)" }}>
                                            <FiSearch size={18} /> SEO Optimization (Optional)
                                        </label>
                                        <p style={{ fontSize: 12, color: "#888", marginBottom: 15 }}>
                                            Override the automatically generated search engine metadata. If left blank, we'll use the product title and description.
                                        </p>
                                        
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                                            <div className="admin-form-field">
                                                <label>Custom Meta Title</label>
                                                <input 
                                                    name="seoTitle" 
                                                    value={form.seoTitle} 
                                                    onChange={handleChange} 
                                                    placeholder="e.g. Best Dark Romance Book | Liliz Black" 
                                                />
                                            </div>
                                            <div className="admin-form-field">
                                                <label>Meta Keywords</label>
                                                <input 
                                                    name="seoKeywords" 
                                                    value={form.seoKeywords} 
                                                    onChange={handleChange} 
                                                    placeholder="dark romance, spicy, mafia, book 1" 
                                                />
                                            </div>
                                        </div>
                                        
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 15 }}>
                                            <div className="admin-form-field">
                                                <label>Meta Catalog Content ID</label>
                                                <input 
                                                    name="metaContentId" 
                                                    value={form.metaContentId || ""} 
                                                    onChange={handleChange} 
                                                    placeholder="e.g. uryqrfk9s9 (Find in Meta Commerce Catalog)" 
                                                />
                                                <span style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                                                    Helps match custom Catalog IDs passed by Meta Shop to this book.
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="admin-form-field full" style={{ marginTop: 10 }}>
                                            <label>Custom Meta Description</label>
                                            <textarea 
                                                name="seoDescription" 
                                                value={form.seoDescription} 
                                                onChange={handleChange} 
                                                placeholder="Enter a compelling summary for Google search results (max 160 chars)..."
                                                style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #e0ddd8", minHeight: 80, fontFamily: "inherit" }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="admin-product-page-footer">
                            <button className="admin-btn-secondary" onClick={closeModal}>Cancel</button>
                            <button className="admin-btn-primary" onClick={handleSave} disabled={saving || uploading}>
                                {saving ? "Saving…" : editId ? "Save Changes" : form.productType === "bundle" ? "Create Bundle" : "Add Product"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
